import React, { useState, useRef, useContext, useEffect } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from "react-icons/rx";
import ModalHeading from '../../components/shared/modal-heading';
import { AccountsContext } from '../../components/context/account-context';
import { prependDefault } from '../../utils/structure-options-utils';
import { uploadMedia } from '../../services/upload-service';
import AttachmentInput from '../../components/shared/attachment-input';
import MultipleAttachmentInput from '../../components/shared/multiple-attachemnt';
import Loader from '../../components/shared/loader';
import { getRenewalMedia, updateRenewal } from '../../services/renewal-service';

const EditModal = ({ selectedRow, setSelectedRow, globalDataloading, setIsEditModalOpen, setData, isVehicleTab = false, vendors, vehicles, types }) => {
    const { accountId, attributes } = useContext(AccountsContext);
    const [imagePreview, setImagePreview] = useState(selectedRow?.expense?.media?.signedThumbUrl || null);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [loadingFields, setLoadingFields] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const [isMultiUploading, setIsMultiUploading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [fetchingMedia, setFetchingMedia] = useState(false);
    const fileInputRef = useRef(null);
    const [error, setError] = useState();

    // Get the renewal media data
    const fetchServicesMedia = async () => {
        try {
            setFetchingMedia(true);
            const res = await getRenewalMedia(accountId, selectedRow.renewalId);
            if (res.success) {
                const mediaFiles = res.data?.data || [];
                setUploadedFiles(mediaFiles.map(file => ({
                    mediaId: file.mediaId,
                    fileType: file.fileType,
                    fileName: file.fileName,
                    signedUrl: file.signedUrl,
                    signedThumbUrl: file.signedThumbUrl,
                    imagePreview: file.fileType?.startsWith('image/') ? file.signedThumbUrl || file.signedUrl : null
                })));
                setError(null);
            } else {
                setError(res.data?.message || 'Failed to fetch media');
            }
        } catch (error) {
            console.error('Error fetching media:', error);
            setError('An error occurred while fetching media');
        } finally {
            setFetchingMedia(false);
        }
    };

    useEffect(() => {
        fetchServicesMedia();
    }, [accountId, selectedRow.renewalId]);


    // Clear status messages after 2 seconds
    useEffect(() => {
        let timer;
        if (uploadStatus.message && uploadStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUploadStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [uploadStatus]);

    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    // Handle multi file upload
    const handleFileUpload = async (files) => {
        if (files.length === 0) return;

        setIsMultiUploading(true);
        setUploadStatus({ message: '', type: '' });

        try {
            // Upload files to the server
            const uploadPromises = files.map((file) =>
                uploadMedia(accountId, file, 'renewal', selectedRow.renewalId)
            );
            const uploadResponses = await Promise.all(uploadPromises);

            console.log("Multi respinse : ", uploadResponses)

            // Process responses
            const successfulUploads = uploadResponses.filter(res => res.success);
            const failedUploads = uploadResponses.filter(res => !res.success);
            console.log(successfulUploads)

            if (failedUploads.length > 0) {
                setUploadStatus({
                    message: `${failedUploads.length} file(s) failed to upload`,
                    type: 'error',
                });
            }

            if (successfulUploads.length > 0) {
                const newFiles = successfulUploads.map(res => ({
                    mediaId: res.data.data.mediaId,
                    fileType: res.data.data.fileType,
                    fileName: res.data.data.fileName,
                    signedUrl: res.data.data.signedUrl,
                    signedThumbUrl: res.data.data.signedThumbUrl,
                    imagePreview: res.data.data.fileType?.startsWith('image/') ? res.data.data.signedThumbUrl || res.data.data.signedUrl : null
                }));

                setUploadedFiles(prev => [...prev, ...newFiles]);
                setUploadStatus({
                    message: `${successfulUploads.length} file(s) uploaded successfully!`,
                    type: 'success',
                });
            }
        } catch (error) {
            console.error('Error uploading media:', error);
            setUploadStatus({
                message: 'Failed to upload files',
                type: 'error',
            });
        } finally {
            setIsMultiUploading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsUploading(true);
            const reader = new FileReader();
            reader.onloadend = async () => {
                setImagePreview(reader.result);

                try {
                    const response = await uploadMedia(accountId, file, 'expense', selectedRow.expense.expenseId);
                    console.log("single respinse : ", response)

                    if (response.success) {
                        const updatedRow = {
                            ...selectedRow,
                            expense: {
                                ...selectedRow.expense,
                                media: response.data
                            }
                        };
                        setSelectedRow(updatedRow);
                        setData(prevData => prevData.map(row =>
                            row.renewalId === selectedRow.renewalId ? updatedRow : row
                        ));
                        setUploadStatus({ message: 'Receipt uploaded successfully!', type: 'success' });
                    } else {
                        setImagePreview(null);
                        setUploadStatus({
                            message: response.data.message || 'Failed to upload receipt',
                            type: 'error'
                        });
                    }
                } finally {
                    setIsUploading(false);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle multi file removal
    const handleRemoveFile = async (fileToRemove) => {
        try {
            setUploadedFiles(prev => prev.filter(file => file.mediaId !== fileToRemove.mediaId));
        } catch (error) {
            console.error('Error removing file:', error);
            // Add it back to the list if deletion failed
            setUploadedFiles(prev => [...prev, fileToRemove]);
            setUploadStatus({
                message: 'Failed to delete file',
                type: 'error',
            });
        }
    };

    const handleRemoveImage = () => {
        if (isUploading) return;
        const updatedRow = {
            ...selectedRow,
            expense: {
                ...selectedRow.expense,
                media: null
            }
        };
        setImagePreview(null);
        setSelectedRow(updatedRow);
        setData(prevData => prevData.map(row =>
            row.renewalId === selectedRow.renewalId ? updatedRow : row
        ));
        setUploadStatus({ message: '', type: '' });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFieldUpdate = async (value, fieldName) => {
        console.log(value, fieldName)
        setLoadingFields(prev => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const fieldMap = {
            startTime: 'startTime',
            endTime: 'endTime',
            vehicle: 'vehicleId',
            vendor: 'vendorId',
            amount: 'amount',
            remind: 'remind',
            type: 'type',
            status: 'status',
            note: 'note'
        };

        const payload = {};
        if (fieldName === 'startTime') {
            payload[fieldMap[fieldName]] = new Date(value).toISOString();
        } else if (fieldName === 'endTime') {
            payload[fieldMap[fieldName]] = new Date(value).toISOString();
        }
        else if (fieldName === 'vendor') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'vehicle') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'amount') {
            payload[fieldMap[fieldName]] = parseFloat(value);
        } else if (fieldName === 'remind') {
            payload[fieldMap[fieldName]] = parseInt(value);
        } else {
            payload[fieldMap[fieldName]] = value;
        }

        try {
            const response = await updateRenewal(accountId, selectedRow.renewalId, payload);
            console.log(response)
            if (response.success) {
                const updatedRow = { ...selectedRow };
                switch (fieldName) {
                    case 'startTime':
                        updatedRow.startTime = payload.startTime;
                        break;
                    case 'endTime':
                        updatedRow.endTime = payload.endTime;
                        break;
                    case 'vendor':
                        updatedRow.vendor = {
                            vendorId: value,
                            name: vendorOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;
                    case 'vehicle':
                        updatedRow.vehicle = {
                            vehicleId: value,
                            name: vehicleOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;

                    case 'amount':
                        updatedRow.expense = { ...updatedRow.expense, amount: parseFloat(value) };
                        break;
                    default:
                        updatedRow[fieldName] = value;
                }
                setSelectedRow(updatedRow);
                setData(prevData => prevData.map(row =>
                    row.serviceId === selectedRow.serviceId ? updatedRow : row
                ));
                setUpdateStatus({ message: `${labelMap[fieldName] || fieldName} updated successfully!`, type: 'success' });
            } else {
                setUpdateStatus({
                    message: response.data.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error'
                });
            }
        } catch (error) {
            setUpdateStatus({
                message: error.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error'
            });
            throw error;
        } finally {
            setLoadingFields(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const labelMap = {
        startTime: 'Start Time',
        endTime: 'End Time',
        vendor: 'Vendor',
        vehicle: 'Vehicle',
        remind: 'Remind(Days)',
        amount: 'Amount',
        type: 'Type',
        status: 'Status',
        note: 'Note'
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const vendorOptions = prependDefault(vendors.map(v => ({ value: v.vendorId, label: v.label })), 'Vendor');
    const vehicleOptions = prependDefault(vehicles.map(v => ({ value: v.vehicleId, label: v.label })), 'Vehicle');
    const typeOptions = prependDefault(types.map(v => ({ value: v.slug, label: v.label })), 'Types');


    return (
        <div className="space-y-3 px-4 pt-3 relative">
            <ModalHeading className="text-left mb-3">Renewal Detail Information</ModalHeading>

            <button
                className='hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full'
                onClick={() => setIsEditModalOpen(false)}
            >
                <RxCross2 className='text-xl' />
            </button>

            {(uploadStatus.message || updateStatus.message) && (
                <div className={`px-4 py-2 text-sm ${(uploadStatus.type || updateStatus.type) === 'success' ? 'text-green-600 bg-green-100' :
                    (uploadStatus.type || updateStatus.type) === 'error' ? 'text-red-600 bg-red-100' :
                        'text-blue-600 bg-blue-100'
                    } rounded-md mx-4`}>
                    {uploadStatus.message || updateStatus.message}
                </div>
            )}

            {error && (
                <div className="text-red-600 bg-red-100 px-4 py-2 rounded-md">
                    {error}
                </div>
            )}

            {(fetchingMedia || globalDataloading) ? (
                <Loader />
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <EditableField
                        label="Start Time"
                        name="startTime"
                        value={selectedRow?.startTime || ''}
                        onChange={handleFieldUpdate}
                        type="datetime-local"
                        isLoading={loadingFields.startTime}
                    />
                    <EditableField
                        label="End Time"
                        name="endTime"
                        value={selectedRow?.endTime || ''}
                        onChange={handleFieldUpdate}
                        type="datetime-local"
                        isLoading={loadingFields.endTime}
                    />

                    <EditableField
                        label="Vehicle"
                        name="vehicle"
                        type='select'
                        value={selectedRow?.vehicle?.vehicleId || ''}
                        options={vehicleOptions}
                        onChange={handleFieldUpdate}
                        isEditable={!isVehicleTab}
                        isLoading={loadingFields.vehicle}
                    />

                    <EditableField
                        label="Vendor"
                        name="vendor"
                        type='select'
                        options={vendorOptions}
                        value={selectedRow?.vendor?.vendorId || ''}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.vendor}
                    />

                    <EditableField
                        label="Type"
                        name="type"
                        type="select"
                        value={selectedRow?.type || ''}
                        onChange={handleFieldUpdate}
                        options={typeOptions}
                        isLoading={loadingFields.type}
                    />

                    <EditableField
                        label="Remind(Days)"
                        name="remind"
                        value={selectedRow?.remind || ''}
                        onChange={handleFieldUpdate}
                        type="number"
                        isLoading={loadingFields.remind}
                    />

                    <EditableField
                        label="Amount"
                        name="amount"
                        value={selectedRow?.expense?.amount || ''}
                        onChange={handleFieldUpdate}
                        type="number"
                        isEditable={false}
                        isLoading={loadingFields.amount}
                        unit={attributes?.currency}
                    />

                    <EditableField
                        label="Status"
                        name="status"
                        value={selectedRow?.status}
                        onChange={handleFieldUpdate}
                        type="select"
                        isEditable={false}
                        isLoading={loadingFields.status}
                    />


                    <EditableField
                        label="Note"
                        name="note"
                        type='textarea'
                        rows={2}
                        value={selectedRow?.note || ''}
                        onChange={handleFieldUpdate}
                        isLoading={loadingFields.note}
                    />

                    <AttachmentInput
                        fileInputRef={fileInputRef}
                        handleImageUpload={handleImageUpload}
                        triggerFileInput={triggerFileInput}
                        imagePreview={imagePreview}
                        selectedRow={selectedRow}
                        handleRemoveImage={handleRemoveImage}
                        isUploading={isUploading}
                        accountId={accountId}
                    />

                    <MultipleAttachmentInput
                        label={'Attach Renewal Media'}
                        handleFileUpload={handleFileUpload}
                        uploadedFiles={uploadedFiles}
                        handleRemoveFile={handleRemoveFile}
                        isUploading={isMultiUploading}
                        accountId={accountId}
                        fileInputRef={fileInputRef}
                    />
                </div>
            )}
        </div>
    );
};

export default EditModal;